/**
 * Legacy imperative engine bridge.
 *
 * `cm2Engine` and `heroControls` are the original landing-page engine — they
 * drive the cost-model animation and hero controls via direct DOM
 * manipulation. They are untyped legacy modules (`@ts-nocheck`) started and
 * reset here; they are NOT React. Components reach the running engine through
 * `heroEngine`. The engine calls back into React through two `window` globals:
 * `openHeroConversionModal` (its "build your own" CTAs → signup) and
 * `MuirLead` (its own inline email gates → React lead handler).
 */
import type { LeadPayload } from '~/widgets/CostBomDemo/hooks/useCostModelLead'

import { initCm2Engine } from '~/widgets/CostBomDemo/engine/cm2Engine'
import { initHeroControls } from '~/widgets/CostBomDemo/engine/heroControls'
import {
  bootGenerate,
  mountBomPanels,
  resetBomPanels,
} from '~/widgets/CostBomDemo/engine/mountBomPanels'

export interface LegacyEngineOptions {
  root: HTMLElement
  productImages: Record<string, string>
  onLead: (lead: LeadPayload) => void
}

let engineInitialized = false

export function runLegacyEngine({
  root,
  productImages,
  onLead,
}: LegacyEngineOptions) {
  if (engineInitialized) return
  engineInitialized = true

  // eslint-disable-next-line no-underscore-dangle
  window.__COST_BOM_PRODUCT_IMAGES__ = productImages
  window.MuirLead = (lead) => {
    onLead({
      email: lead.email,
      product: lead.product,
      country: lead.country,
      source: lead.source as LeadPayload['source'],
    })
  }

  mountBomPanels(root)
  initCm2Engine()
  initHeroControls()
  bootGenerate(root)
}

export function resetLegacyEngine() {
  engineInitialized = false
  resetBomPanels()
}

declare global {
  interface Window {
    __COST_BOM_PRODUCT_IMAGES__?: Record<string, string>
    MuirLead?: (lead: {
      email: string
      product: string
      country: string
      source: string
    }) => void
    openHeroConversionModal?: () => void
  }
}
